import React, {
    useState,
    useEffect,
} from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import { Collapse } from 'wanderu-animations';
import {
    combineClasses,
} from 'wanderu-css';

import {
    ChevronDownIcon,
    ChevronUpIcon,
} from '../Icons';
import Typography from '../Typography/Typography';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './Accordion.scss';

function Heading(props) {
    const {
        title,
        className,
    } = props;

    if (typeof title === 'string') {
        return (
            <Typography
                className={className}
                variant="body1"
            >
                {title}
            </Typography>
        );
    }

    return (
        <span className={className}>
            {title}
        </span>
    );
}

function Accordion(props) {
    const {
        classes: classesProp,
        component: ComponentProp = 'div',
        componentProps = {},
        id,
        title,
        iconClass,
        headerClass,
        containerClass,
        contentClass,
        children,
        collapseClassname,
        isOpen: defaultIsOpen = true,
    } = props;
    const [isOpen, toggleAccordion] = useState(defaultIsOpen);
    const [hasMounted, toggleMounted] = useState(false);

    useEffect(() => toggleMounted(true), [toggleMounted]);

    function onClick() {
        toggleAccordion(!isOpen);
    }

    let Component = ComponentProp;

    const classes = combineClasses(styles, classesProp);

    return (
        <Component
            className={containerClass}
            {...componentProps}
        >
            <button
                aria-controls={`${id}-content`}
                aria-expanded={hasMounted
                    ? isOpen
                    : false}
                className={classNames(classes.accordionHeader, headerClass)}
                id={id}
                onClick={onClick}
                type="button"
            >
                <Heading className={classes.title} title={title} />
                <span aria-hidden="true" className={classNames(classes.icon, iconClass)} role="img">
                    <ChevronUpIcon
                        className={classNames(
                            classes.chevron,
                            {
                                [styles.chevronUpDefault]: !hasMounted,
                                [styles.show]: hasMounted
                                    ? isOpen
                                    : true,
                            },
                        )}
                    />
                    <ChevronDownIcon
                        className={classNames(
                            classes.chevron,
                            {
                                [styles.chevronDownDefault]: !hasMounted,
                                [styles.show]: hasMounted
                                    ? !isOpen
                                    : false,
                            },
                        )}
                    />
                </span>
            </button>
            <Collapse
                childClassName={classNames({ [styles.collapseChild]: !hasMounted })}
                className={classNames(collapseClassname, {
                    [styles.collapse]: !hasMounted,
                })}
                duration={300}
                isOpen={hasMounted
                    ? isOpen
                    : false}
                key={hasMounted}
            >
                <div
                    aria-labelledby={id}
                    className={contentClass}
                    id={`${id}-content`}
                    role="region"
                >
                    {children}
                </div>
            </Collapse>
        </Component>
    );
}

Accordion.propTypes = {
    children: PropTypes.node,
    classes: PropTypes.object,
    className: PropTypes.string,
    containerClass: PropTypes.string,
    contentClass: PropTypes.string,
    headerClass: PropTypes.string,
    iconClass: PropTypes.string,
    id: PropTypes.string.isRequired,
    component: PropTypes.elementType,
    componentProps: PropTypes.object,
    isOpen: PropTypes.bool,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
    label: PropTypes.string,
};

export default Accordion;
